import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { GripVertical, Edit, Trash2 } from 'lucide-react';

const BLOCK_ICONS = {
  text: '📝',
  image: '🖼️',
  text_image: '🖌️',
  stats: '📊',
  card: '🎴',
  hero: '🎆'
};

const SortableBlock = ({ block, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const renderBlockPreview = () => {
    switch (block.type) {
      case 'hero':
        return (
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 rounded-lg">
            <h2 className="text-3xl font-bold mb-2">{block.content.title || 'Titre Hero'}</h2>
            <p className="text-lg mb-4">{block.content.subtitle || 'Sous-titre'}</p>
            {block.content.cta_text && (
              <button className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold">
                {block.content.cta_text}
              </button>
            )}
          </div>
        );
      
      case 'text':
        return (
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-2">{block.content.title || 'Titre'}</h3>
            <p className="text-gray-600">{block.content.text || 'Texte du bloc...'}</p>
          </div>
        );
      
      case 'image':
        return (
          <div className="p-4">
            {block.content.image_url ? (
              <img src={block.content.image_url} alt={block.content.alt || 'Image'} className="w-full rounded-lg" />
            ) : (
              <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-gray-400">Image</span>
              </div>
            )}
            {block.content.caption && (
              <p className="text-sm text-gray-500 mt-2">{block.content.caption}</p>
            )}
          </div>
        );
      
      case 'stats':
        return (
          <div className="p-6">
            <div className="grid grid-cols-3 gap-4">
              {(block.content.items || []).map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-3xl font-bold text-blue-600">{item.value}</div>
                  <div className="text-sm text-gray-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'text_image':
        return (
          <div className="p-6 grid grid-cols-2 gap-4 md:gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">{block.content.title || 'Titre'}</h3>
              <p className="text-gray-600">{block.content.text || 'Texte...'}</p>
            </div>
            <div className="bg-gray-200 rounded-lg h-48 flex items-center justify-center">
              <span className="text-gray-400">Image</span>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="p-6">
            <p className="text-gray-400">Bloc {block.type}</p>
          </div>
        );
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <Card className="border-2 border-transparent hover:border-blue-500 transition-all">
        {/* Block Header */}
        <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
          <div className="flex items-center gap-2">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing hover:bg-gray-200 p-1 rounded"
            >
              <GripVertical className="h-5 w-5 text-gray-400" />
            </div>
            <span className="text-2xl">{BLOCK_ICONS[block.type] || '📄'}</span>
            <div>
              <p className="text-sm font-semibold">{block.name}</p>
              <p className="text-xs text-gray-500">{block.type}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </div>

        {/* Block Preview */}
        <div className="bg-white">
          {renderBlockPreview()}
        </div>
      </Card>
    </div>
  );
};

export default SortableBlock;